import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 10; // ~20s @ 2s

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    let fired = false;
    const fireConversion = (data) => {
      if (fired || typeof window === "undefined" || typeof window.gtag !== "function") return;
      fired = true;
      try {
        window.gtag("event", "conversion", {
          send_to: "AW-18347487987/fq-UCIq0h9YcEPPl4axE",
          value: Number(data.amount) || 1.0,
          currency: (data.currency || "USD").toUpperCase(),
          transaction_id: data.session_id || sessionId,
        });
      } catch (e) {
        /* no-op */
      }
    };
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          fireConversion(data);
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        // ignore; keep polling
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A1428] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div className="hero-orb absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-10 md:p-12 text-center"
        data-testid="payment-success-card"
      >
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={BRAND.logo} alt={BRAND.name} className="h-11 w-11 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg">TITAN<span className="text-[#1E6BFF]">.</span></span>
        </div>

        {status === "polling" && (
          <>
            <Loader2 size={48} className="text-[#1E6BFF] animate-spin mx-auto mb-6" />
            <h1 className="font-display font-bold text-3xl mb-3">Confirming your payment…</h1>
            <p className="text-gray-400">This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#1E6BFF]/15 border border-[#1E6BFF]/40 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={44} className="text-[#1E6BFF]" />
            </div>
            <h1 className="font-display font-bold text-3xl mb-3">Payment received 🎉</h1>
            <p className="text-gray-300 leading-relaxed mb-8">
              Thanks for choosing Titan Web Solutions. We've received your payment and will email you within
              one business day to kick off your project.
            </p>
            <Link
              to="/"
              data-testid="success-home-link"
              className="inline-flex items-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white font-semibold px-8 py-3.5 transition-colors duration-200 glow-blue"
            >
              Back to Home <ArrowUpRight size={18} />
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <XCircle size={48} className="text-amber-400 mx-auto mb-6" />
            <h1 className="font-display font-bold text-2xl mb-3">
              {status === "timeout" ? "Still confirming…" : "Something went wrong"}
            </h1>
            <p className="text-gray-400 leading-relaxed mb-8">
              {status === "timeout"
                ? "Your payment may still be processing. You'll receive an email as soon as it's confirmed."
                : "We couldn't verify your payment. If money was taken, contact us and we'll sort it out."}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/50 text-white font-semibold px-8 py-3.5 transition-colors duration-200"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
